// ============================================================================
// API ENDPOINT CONFIGURATION
// Centralized API endpoint definitions for the task management module
// ============================================================================

const BASE_PATH = '/task_management';

export const API_ENDPOINTS = {
  // =========================================================================
  // AUTHENTICATION
  // =========================================================================
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },

  // =========================================================================
  // TASKS
  // =========================================================================
  TASKS: {
    LIST: `${BASE_PATH}/tasks`,
    GET: (id: string) => `${BASE_PATH}/tasks/${id}`,
    CREATE: `${BASE_PATH}/tasks`,
    UPDATE: (id: string) => `${BASE_PATH}/tasks/${id}`,
    DELETE: (id: string) => `${BASE_PATH}/tasks/${id}`,
    UPDATE_STATUS: (id: string) => `${BASE_PATH}/tasks/${id}/status`,
    REORDER: `${BASE_PATH}/tasks/reorder`,
    
    // Subtasks
    SUBTASKS: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/subtasks`,
    
    // Comments
    COMMENTS: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/comments`,
    ADD_COMMENT: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/comments`,
    
    // Attachments
    ATTACHMENTS: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/attachments`,
    UPLOAD_ATTACHMENT: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/attachments`,
    DELETE_ATTACHMENT: (taskId: string, attachmentId: string) =>
      `${BASE_PATH}/tasks/${taskId}/attachments/${attachmentId}`,
    
    // Assignment
    ASSIGN: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/assign`,
    UNASSIGN: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/unassign`,
    
    // Linked issues
    ISSUES: (taskId: string) => `${BASE_PATH}/tasks/${taskId}/issues`,
    ISSUE: (taskId: string, issueId: string) => `${BASE_PATH}/tasks/${taskId}/issues/${issueId}`,
    
    // My tasks
    MY_TASKS: `${BASE_PATH}/tasks/my-tasks`,
    TODAY: `${BASE_PATH}/tasks/today`,
    OVERDUE: `${BASE_PATH}/tasks/overdue`,
    UPCOMING: `${BASE_PATH}/tasks/upcoming`,
    LIVE: `${BASE_PATH}/tasks/live`,
  },

  // =========================================================================
  // PROJECTS
  // =========================================================================
  PROJECTS: {
    LIST: `${BASE_PATH}/projects`,
    GET: (id: string) => `${BASE_PATH}/projects/${id}`,
    CREATE: `${BASE_PATH}/projects`,
    UPDATE: (id: string) => `${BASE_PATH}/projects/${id}`,
    DELETE: (id: string) => `${BASE_PATH}/projects/${id}`,
    
    // Project tasks
    TASKS: (projectId: string) => `${BASE_PATH}/projects/${projectId}/tasks`,
    ADD_TASK: (projectId: string) => `${BASE_PATH}/projects/${projectId}/tasks`,
    
    // Milestones
    MILESTONES: (projectId: string) => `${BASE_PATH}/projects/${projectId}/milestones`,
    ADD_MILESTONE: (projectId: string) => `${BASE_PATH}/projects/${projectId}/milestones`,
    UPDATE_MILESTONE: (projectId: string, milestoneId: string) =>
      `${BASE_PATH}/projects/${projectId}/milestones/${milestoneId}`,
    DELETE_MILESTONE: (projectId: string, milestoneId: string) =>
      `${BASE_PATH}/projects/${projectId}/milestones/${milestoneId}`,
    
    // Stats
    STATS: (projectId: string) => `${BASE_PATH}/projects/${projectId}/stats`,
    HEALTH: `${BASE_PATH}/projects/health`,
  },

  // =========================================================================
  // TIME TRACKING
  // =========================================================================
  TIME: {
    LOGS: `${BASE_PATH}/time-logs`,
    TASK_LOGS: (taskId: string) => `${BASE_PATH}/time-logs/task/${taskId}`,
    USER_LOGS: (userId: string) => `${BASE_PATH}/time-logs/user/${userId}`,
    
    ACTIVE: `${BASE_PATH}/time-logs/active`,
    START: `${BASE_PATH}/time-logs/start`,
    STOP: `${BASE_PATH}/time-logs/stop`,
    PAUSE: `${BASE_PATH}/time-logs/pause`,
    RESUME: `${BASE_PATH}/time-logs/resume`,
    
    MANUAL_ENTRY: `${BASE_PATH}/time-logs/manual`,
    CORRECT: (logId: string) => `${BASE_PATH}/time-logs/${logId}/correct`,
    
    DAILY_SUMMARY: `${BASE_PATH}/time-logs/daily-summary`,
    WEEKLY_SUMMARY: `${BASE_PATH}/time-logs/weekly-summary`,
  },

  // =========================================================================
  // DAILY PERFORMANCE
  // =========================================================================
  PERFORMANCE: {
    DAILY: `${BASE_PATH}/performance/daily`,
    GET_DATE: (date: string) => `${BASE_PATH}/performance/daily/${date}`,
    SUBMIT_MISS_REASON: `${BASE_PATH}/performance/miss-reason`,
    WEEKLY: `${BASE_PATH}/performance/weekly`,
    MONTHLY: `${BASE_PATH}/performance/monthly`,
    USER_STATS: (userId: string) => `${BASE_PATH}/performance/user/${userId}`,
    TEAM_STATS: (teamId: string) => `${BASE_PATH}/performance/team/${teamId}`,
  },

  // =========================================================================
  // BANDWIDTH
  // =========================================================================
  BANDWIDTH: {
    MY_BANDWIDTH: `${BASE_PATH}/bandwidth/me`,
    MY_WEEKLY_BANDWIDTH: `${BASE_PATH}/bandwidth/me/weekly`,
    USER_BANDWIDTH: (userId: string) => `${BASE_PATH}/bandwidth/user/${userId}`,
    TEAM_BANDWIDTH: (teamId: string) => `${BASE_PATH}/bandwidth/team/${teamId}`,
    CHECK_AVAILABILITY: `${BASE_PATH}/bandwidth/check-availability`,
    FORECAST: `${BASE_PATH}/bandwidth/forecast`,
  },

  // =========================================================================
  // ATTENDANCE
  // =========================================================================
  ATTENDANCE: {
    MY_ATTENDANCE: `${BASE_PATH}/attendance/me`,
    USER_ATTENDANCE: (userId: string) => `${BASE_PATH}/attendance/user/${userId}`,
    TEAM_ATTENDANCE: (teamId: string) => `${BASE_PATH}/attendance/team/${teamId}`,
    OVERRIDE: (attendanceId: string) => `${BASE_PATH}/attendance/${attendanceId}/override`,
    REPORT: `${BASE_PATH}/attendance/report`,
  },

  // =========================================================================
  // LEAVE
  // =========================================================================
  LEAVE: {
    REQUESTS: `${BASE_PATH}/leave/requests`,
    MY_REQUESTS: `${BASE_PATH}/leave/requests/me`,
    GET: (id: string) => `${BASE_PATH}/leave/requests/${id}`,
    CREATE: `${BASE_PATH}/leave/requests`,
    CANCEL: (id: string) => `${BASE_PATH}/leave/requests/${id}/cancel`,
    
    // Manager actions
    APPROVE: (id: string) => `${BASE_PATH}/leave/requests/${id}/approve`,
    REJECT: (id: string) => `${BASE_PATH}/leave/requests/${id}/reject`,
    PENDING_APPROVALS: `${BASE_PATH}/leave/requests/pending-approvals`,
    
    // Impact
    IMPACT_ANALYSIS: `${BASE_PATH}/leave/impact-analysis`,
    
    // Balance
    BALANCE: `${BASE_PATH}/leave/balance`,
    USER_BALANCE: (userId: string) => `${BASE_PATH}/leave/balance/${userId}`,
    
    // Calendar
    CALENDAR: `${BASE_PATH}/leave/calendar`,
    TEAM_CALENDAR: (teamId: string) => `${BASE_PATH}/leave/calendar/team/${teamId}`,
  },

  // =========================================================================
  // REVIEWS
  // =========================================================================
  REVIEWS: {
    LIST: `${BASE_PATH}/reviews`,
    GET: (id: string) => `${BASE_PATH}/reviews/${id}`,
    CREATE: `${BASE_PATH}/reviews`,
    
    // Task reviews
    TASK_REVIEW: (taskId: string) => `${BASE_PATH}/reviews/task/${taskId}`,
    SUBMIT_TASK_REVIEW: (taskId: string) => `${BASE_PATH}/reviews/task/${taskId}/submit`,
    
    // Actions
    APPROVE: (reviewId: string) => `${BASE_PATH}/reviews/${reviewId}/approve`,
    REQUEST_REWORK: (reviewId: string) => `${BASE_PATH}/reviews/${reviewId}/rework`,
    REJECT: (reviewId: string) => `${BASE_PATH}/reviews/${reviewId}/reject`,
    
    // Comments
    COMMENTS: (reviewId: string) => `${BASE_PATH}/reviews/${reviewId}/comments`,
    ADD_COMMENT: (reviewId: string) => `${BASE_PATH}/reviews/${reviewId}/comments`,
    
    // Queue
    MY_PENDING: `${BASE_PATH}/reviews/pending/me`,
    TEAM_PENDING: `${BASE_PATH}/reviews/pending/team`,
  },

  // =========================================================================
  // USERS & TEAMS
  // =========================================================================
  USERS: {
    LIST: `${BASE_PATH}/users`,
    GET: (id: string) => `${BASE_PATH}/users/${id}`,
    ME: `${BASE_PATH}/users/me`,
    SEARCH: `${BASE_PATH}/users/search`,
    BY_TEAM: (teamId: string) => `${BASE_PATH}/users/team/${teamId}`,
    BY_DEPARTMENT: (deptId: string) => `${BASE_PATH}/users/department/${deptId}`,
    TEAM_MEMBERS_WORKLOAD: `${BASE_PATH}/users/team-members-workload`,
    TEAM_MEMBERS_FILTERED: `${BASE_PATH}/users/team-members-filtered`,
    CREATE: `${BASE_PATH}/users`,
    UPDATE: (id: string) => `${BASE_PATH}/users/${id}`,
    DELETE: (id: string) => `${BASE_PATH}/users/${id}`,
  },

  TEAMS: {
    LIST: `${BASE_PATH}/teams`,
    GET: (id: string) => `${BASE_PATH}/teams/${id}`,
    MEMBERS: (teamId: string) => `${BASE_PATH}/teams/${teamId}/members`,
    WORKLOAD: (teamId: string) => `${BASE_PATH}/teams/${teamId}/workload`,
  },

  DEPARTMENTS: {
    LIST: `${BASE_PATH}/departments`,
    GET: (id: string) => `${BASE_PATH}/departments/${id}`,
    COMPARISON: `${BASE_PATH}/departments/comparison`,
    PERFORMANCE: (id: string) => `${BASE_PATH}/departments/${id}/performance`,
    TASK_DISTRIBUTION: (id: string) => `${BASE_PATH}/departments/${id}/task-distribution`,
    MEMBER_WORKLOAD: (id: string) => `${BASE_PATH}/departments/${id}/member-workload`,
    TEAMS: (id: string) => `${BASE_PATH}/departments/${id}/teams`,
  },

  // =========================================================================
  // DELEGATED TASKS
  // =========================================================================
  DELEGATED_TASKS: {
    LIST: `${BASE_PATH}/delegated-tasks`,
    GET: (id: string) => `${BASE_PATH}/delegated-tasks/${id}`,
    CREATE: `${BASE_PATH}/delegated-tasks`,
    UPDATE: (id: string) => `${BASE_PATH}/delegated-tasks/${id}`,
    DELETE: (id: string) => `${BASE_PATH}/delegated-tasks/${id}`,
    STATUS: (id: string) => `${BASE_PATH}/delegated-tasks/${id}/status`,
    PROGRESS: (id: string) => `${BASE_PATH}/delegated-tasks/${id}/progress`,
    FEEDBACK: (id: string) => `${BASE_PATH}/delegated-tasks/${id}/feedback`,
  },

  // =========================================================================
  // DASHBOARD
  // =========================================================================
  DASHBOARD: {
    INDIVIDUAL: `${BASE_PATH}/dashboard/individual`,
    MANAGER: `${BASE_PATH}/dashboard/manager`,
    PROJECT_MANAGER: `${BASE_PATH}/dashboard/project-manager`,
    ADMIN: `${BASE_PATH}/dashboard/admin`,
    WIDGETS: `${BASE_PATH}/dashboard/widgets`,
  },

  // =========================================================================
  // NOTIFICATIONS
  // =========================================================================
  NOTIFICATIONS: {
    LIST: `${BASE_PATH}/notifications`,
    UNREAD: `${BASE_PATH}/notifications/unread`,
    MARK_READ: (id: string) => `${BASE_PATH}/notifications/${id}/read`,
    MARK_ALL_READ: `${BASE_PATH}/notifications/mark-all-read`,
    CLEAR_READ: `${BASE_PATH}/notifications/clear-read`,
    PREFERENCES: `${BASE_PATH}/notifications/preferences`,
  },

  // =========================================================================
  // CALENDAR
  // =========================================================================
  CALENDAR: {
    EVENTS: `${BASE_PATH}/calendar/events`,
    MY_EVENTS: `${BASE_PATH}/calendar/events/me`,
    TEAM_EVENTS: (teamId: string) => `${BASE_PATH}/calendar/events/team/${teamId}`,
  },

  // =========================================================================
  // KRA / KPI (Feature 07)
  // =========================================================================
  KRAS: {
    LIST: `${BASE_PATH}/kras`,
    GET: (id: string) => `${BASE_PATH}/kras/${id}`,
    CREATE: `${BASE_PATH}/kras`,
    UPDATE: (id: string) => `${BASE_PATH}/kras/${id}`,
    DELETE: (id: string) => `${BASE_PATH}/kras/${id}`,
    KPIS: (kraId: string) => `${BASE_PATH}/kras/${kraId}/kpis`,
    CREATE_KPI: (kraId: string) => `${BASE_PATH}/kras/${kraId}/kpis`,
  },
  KPIS: {
    GET: (id: string) => `${BASE_PATH}/kpis/${id}`,
    UPDATE: (id: string) => `${BASE_PATH}/kpis/${id}`,
    DELETE: (id: string) => `${BASE_PATH}/kpis/${id}`,
  },

  // =========================================================================
  // PROJECT TEMPLATES (Feature 05)
  // =========================================================================
  PROJECT_TEMPLATES: {
    LIST: `${BASE_PATH}/project-templates`,
    GET: (id: string) => `${BASE_PATH}/project-templates/${id}`,
  },

  // =========================================================================
  // PERMISSIONS (Dynamic RBAC)
  // =========================================================================
  PERMISSIONS: {
    STRUCTURE: `${BASE_PATH}/permissions/structure`,
    PAGES: `${BASE_PATH}/permissions/pages`,
    PAGES_ME: `${BASE_PATH}/permissions/pages/me`,
    PAGE_CHECK: (pageKey: string) => `${BASE_PATH}/permissions/pages/check/${pageKey}`,
    UPDATE_PAGE: (role: string) => `${BASE_PATH}/permissions/pages/${role}`,
    FEATURES: `${BASE_PATH}/permissions/features`,
    FEATURES_ME: `${BASE_PATH}/permissions/features/me`,
    FEATURES_BY_PAGE: (pageKey: string) => `${BASE_PATH}/permissions/features/${pageKey}`,
    FEATURE_CHECK: (pageKey: string, featureKey: string, action: string) =>
      `${BASE_PATH}/permissions/features/check/${pageKey}/${featureKey}/${action}`,
    UPDATE_FEATURE: (role: string, pageKey: string) =>
      `${BASE_PATH}/permissions/features/${role}/${pageKey}`,
    LOGS: `${BASE_PATH}/permissions/logs`,
  },

  // =========================================================================
  // SYSTEM CONFIGURATION (Admin Settings)
  // =========================================================================
  CONFIG: {
    BASE: `${BASE_PATH}/config`,
    BY_TYPE: (type: string) => `${BASE_PATH}/config/type/${type}`,
    GET: (key: string) => `${BASE_PATH}/config/${key}`,
    UPDATE: (key: string) => `${BASE_PATH}/config/${key}`,
    RESET: (key: string) => `${BASE_PATH}/config/${key}/reset`,
    HISTORY: (key: string) => `${BASE_PATH}/config/${key}/history`,
  },
};

export default API_ENDPOINTS;
